import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../lib/auth";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ResetWorkspaceButton from "../../components/app/ResetWorkspaceButton";

export default function OrganizationSettings() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const canEdit = ["owner", "admin"].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/organization");
        setOrg(data); setName(data.name); setBusinessName(data.business_name || data.name);
      } catch {}
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", { name, business_name: businessName });
      setOrg(data); toast.success("Organization updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/uploads/organization-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/organization");
      setOrg(data);
      toast.success("Logo updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  if (!org) return null;

  return (
    <div className="space-y-8" data-testid="organization-settings">
      <div>
        <h2 className="text-xl font-medium text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Organization</h2>
        <p className="text-sm text-zinc-500 mt-1">Workspace identity visible to your team.</p>
      </div>

      <div className="flex items-center gap-5">
        <div className="relative h-20 w-20 rounded-xl bg-zinc-800 overflow-hidden flex items-center justify-center text-2xl text-zinc-300">
          {org.logo ? <img src={fileUrl(org.logo)} alt="" className="h-full w-full object-cover" /> : (org.name?.charAt(0).toUpperCase() || "?")}
          {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
        </div>
        {canEdit && (
          <div>
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 px-3 py-1.5 rounded-lg text-sm text-zinc-200" data-testid="org-upload-btn">
              <Camera className="h-3.5 w-3.5" /> Change logo
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} />
            <p className="text-xs text-zinc-500 mt-2">PNG, JPG, WebP up to 5 MB.</p>
          </div>
        )}
      </div>

      <form onSubmit={save} className="space-y-5 max-w-md">
        <div>
          <label className="text-xs font-medium text-zinc-400">Workspace name</label>
          <input value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 disabled:opacity-50" data-testid="org-name-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-400">Business name</label>
          <input value={businessName} disabled={!canEdit} onChange={(e) => setBusinessName(e.target.value)} className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 disabled:opacity-50" data-testid="org-business-input" />
        </div>
        {canEdit && (
          <button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid="org-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </button>
        )}
      </form>

      {/* Danger zone — Reset Workspace */}
      {user?.role === "owner" && (
        <section className="pt-8 mt-8 border-t border-zinc-800" data-testid="org-danger-zone">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-red-400 mb-2">Danger zone</h3>
          <p className="text-sm text-zinc-400 max-w-lg mb-4">
            Permanently delete every import, order, product, customer and AI briefing in this workspace. Your team, roles and organization settings will remain untouched.
          </p>
          <ResetWorkspaceButton variant="danger" testid="settings-reset-workspace" />
        </section>
      )}
    </div>
  );
}
